import { useRef, useCallback, useEffect } from 'react';

/**
 * Click/drag on minimap to navigate the main canvas.
 * Uses direct DOM manipulation + rAF coalescing during drag for performance.
 */
export function useMinimapPanInteraction({
  minimapRef,
  containerRef,
  containerSizeRef,
  panRef,
  zoomRef,
  constrainPan,
  bounds,
  scale,
  minimapWidth,
  minimapHeight,
  onPanChange,
  onPanChangeAnimated,
}) {
  const constrainPanRef = useRef(constrainPan);
  constrainPanRef.current = constrainPan;

  const minimapDraggingRef = useRef(false);
  const lastPanRef = useRef({ x: 0, y: 0 });
  const mainCanvasRef = useRef(null);
  const pendingMinimapPanRef = useRef(null);
  const minimapPanRafRef = useRef(null);
  const pendingRawPanForRafRef = useRef(null);
  const minimapDocListenersRef = useRef({ move: null, up: null });
  const handleMinimapInteractionRef = useRef(null);
  const minimapGrabOffsetRef = useRef(null);

  const calculatePanFromMinimapPos = useCallback(
    (e) => {
      if (!minimapRef.current) return null;

      const rect = minimapRef.current.getBoundingClientRect();
      const containerSize = containerSizeRef.current;
      const currentZoom = zoomRef.current;

      const clickX = Math.max(0, Math.min(e.clientX - rect.left, minimapWidth));
      const clickY = Math.max(0, Math.min(e.clientY - rect.top, minimapHeight));

      const grab = minimapGrabOffsetRef.current;
      if (
        grab &&
        containerSize.width > 0 &&
        containerSize.height > 0 &&
        currentZoom > 0
      ) {
        const vx = clickX - grab.offX;
        const vy = clickY - grab.offY;
        const newPanX = -(bounds.minX + vx / scale) * currentZoom;
        const newPanY = -(bounds.minY + vy / scale) * currentZoom;
        return { x: newPanX, y: newPanY };
      }

      const canvasX = clickX / scale + bounds.minX;
      const canvasY = clickY / scale + bounds.minY;

      const newPanX = -(canvasX * currentZoom - containerSize.width / 2);
      const newPanY = -(canvasY * currentZoom - containerSize.height / 2);

      return { x: newPanX, y: newPanY };
    },
    [scale, bounds.minX, bounds.minY, minimapWidth, minimapHeight, minimapRef, containerSizeRef, zoomRef],
  );

  const applyMinimapPanFrame = useCallback(() => {
    const newPan = pendingMinimapPanRef.current;
    if (!newPan) return;
    pendingMinimapPanRef.current = null;

    const flowchart = containerRef?.current;
    if (!flowchart) return;

    const currentZoom = zoomRef.current;
    const canvas =
      mainCanvasRef.current ||
      (mainCanvasRef.current = flowchart.querySelector('.flowchart__canvas'));
    if (canvas) {
      canvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${currentZoom})`;
      // Writing this every move restyles every card. Zoom does not change while dragging.
      const inverse = String(1 / currentZoom);
      if (canvas.dataset.inverseZoom !== inverse) {
        canvas.dataset.inverseZoom = inverse;
        canvas.style.setProperty('--inverse-zoom', inverse);
      }
      canvas.dispatchEvent(
        new CustomEvent('panupdate', {
          detail: { x: newPan.x, y: newPan.y, zoom: currentZoom },
        }),
      );
    }
  }, [containerRef, zoomRef]);

  const flushMinimapPanFromPendingRaw = useCallback(() => {
    const rawPan = pendingRawPanForRafRef.current;
    pendingRawPanForRafRef.current = null;
    if (!rawPan) return;

    const flowchart = containerRef?.current;
    if (!flowchart) return;

    const currentZoom = zoomRef.current;

    const constrained = constrainPanRef.current
      ? constrainPanRef.current(rawPan, currentZoom)
      : rawPan;

    const RUBBER_BAND_FACTOR = 0.3;
    const overshootX = rawPan.x - constrained.x;
    const overshootY = rawPan.y - constrained.y;

    const newPan = {
      x: constrained.x + overshootX * RUBBER_BAND_FACTOR,
      y: constrained.y + overshootY * RUBBER_BAND_FACTOR,
    };

    lastPanRef.current = newPan;
    pendingMinimapPanRef.current = newPan;
    applyMinimapPanFrame();
  }, [containerRef, applyMinimapPanFrame, zoomRef]);

  const updateCanvasDirectly = useCallback(
    (rawPan) => {
      pendingRawPanForRafRef.current = rawPan;
      // Same turn as the mouse event. Waiting for a frame is the hitch at the start of a drag.
      flushMinimapPanFromPendingRaw();
    },
    [flushMinimapPanFromPendingRaw],
  );

  const handleMinimapInteraction = useCallback(
    (e) => {
      const newPan = calculatePanFromMinimapPos(e);
      if (!newPan) return;

      if (minimapDraggingRef.current) {
        updateCanvasDirectly(newPan);
      } else {
        onPanChange?.(newPan);
      }
    },
    [calculatePanFromMinimapPos, updateCanvasDirectly, onPanChange],
  );

  handleMinimapInteractionRef.current = handleMinimapInteraction;

  const detachMinimapDocListeners = useCallback(() => {
    const { move, up } = minimapDocListenersRef.current;
    if (move) document.removeEventListener('mousemove', move);
    if (up) document.removeEventListener('mouseup', up, true);
    minimapDocListenersRef.current = { move: null, up: null };
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      detachMinimapDocListeners();
      if (minimapPanRafRef.current != null) {
        cancelAnimationFrame(minimapPanRafRef.current);
        minimapPanRafRef.current = null;
      }

      minimapDraggingRef.current = true;
      minimapRef.current?.classList.add('flowchart-minimap__canvas--dragging');
      containerRef?.current?.classList.add('flowchart--panning');

      const onMove = (ev) => {
        if (!minimapDraggingRef.current) return;
        handleMinimapInteractionRef.current?.(ev);
      };
      const onUp = () => {
        if (!minimapDraggingRef.current) return;
        minimapDraggingRef.current = false;
        minimapRef.current?.classList.remove('flowchart-minimap__canvas--dragging');
        containerRef?.current?.classList.remove('flowchart--panning');
        detachMinimapDocListeners();
        if (minimapPanRafRef.current != null) {
          cancelAnimationFrame(minimapPanRafRef.current);
          minimapPanRafRef.current = null;
        }
        minimapGrabOffsetRef.current = null;
        flushMinimapPanFromPendingRaw();
        if (onPanChangeAnimated) {
          onPanChangeAnimated(lastPanRef.current);
        } else {
          onPanChange?.(lastPanRef.current);
        }
        window.dispatchEvent(new CustomEvent('flowchartpanend'));
      };

      minimapDocListenersRef.current = { move: onMove, up: onUp };
      document.addEventListener('mousemove', onMove, { passive: true });
      document.addEventListener('mouseup', onUp, { capture: true });

      if (minimapRef.current) {
        const mr = minimapRef.current.getBoundingClientRect();
        const cx = Math.max(0, Math.min(e.clientX - mr.left, minimapWidth));
        const cy = Math.max(0, Math.min(e.clientY - mr.top, minimapHeight));
        const containerSize = containerSizeRef.current;
        const currentZoom = zoomRef.current;
        const panNow = panRef.current;
        if (containerSize.width > 0 && containerSize.height > 0 && currentZoom > 0) {
          const canvasLeft = -panNow.x / currentZoom;
          const canvasTop = -panNow.y / currentZoom;
          const canvasWidth = containerSize.width / currentZoom;
          const canvasHeight = containerSize.height / currentZoom;
          const vx0 = (canvasLeft - bounds.minX) * scale;
          const vy0 = (canvasTop - bounds.minY) * scale;
          const vw = canvasWidth * scale;
          const vh = canvasHeight * scale;
          const insideViewport =
            cx >= vx0 && cx <= vx0 + vw && cy >= vy0 && cy <= vy0 + vh;
          minimapGrabOffsetRef.current = insideViewport
            ? { offX: cx - vx0, offY: cy - vy0 }
            : null;
        } else {
          minimapGrabOffsetRef.current = null;
        }
      } else {
        minimapGrabOffsetRef.current = null;
      }

      handleMinimapInteraction(e);
    },
    [
      containerRef,
      detachMinimapDocListeners,
      onPanChange,
      onPanChangeAnimated,
      handleMinimapInteraction,
      flushMinimapPanFromPendingRaw,
      minimapWidth,
      minimapHeight,
      bounds.minX,
      bounds.minY,
      scale,
      minimapRef,
      containerSizeRef,
      panRef,
      zoomRef,
    ],
  );

  useEffect(
    () => () => {
      detachMinimapDocListeners();
      if (minimapPanRafRef.current != null) {
        cancelAnimationFrame(minimapPanRafRef.current);
        minimapPanRafRef.current = null;
      }
    },
    [detachMinimapDocListeners],
  );

  return { handleMouseDown };
}
